import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, message as antMessage, Spin } from 'antd';
import {
  MenuOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';

// Components
import WelcomeScreen from './components/chat/WelcomeScreen';
import MessageList from './components/chat/MessageList';
import ChatInput from './components/chat/ChatInput';
import ConversationSidebar from './components/conversations/ConversationSidebar';
import SuggestionCards from './components/suggestions/SuggestionCards';

// Hooks
import { useStreamingResponse } from './hooks/useStreamingResponse';
import { useConversations } from './hooks/useConversations';

// Services
import aiSummarizationService from '../../services/aiSummarizationService';

// Styles
import styles from './styles/chatV2.module.css';

/**
 * AICopilotChatV2 - Modernized Chat Interface
 *
 * Features:
 * - Streaming responses (SSE)
 * - Inline source citations
 * - Message actions (copy, regenerate, feedback, share)
 * - Conversation history with search
 * - Welcome screen with examples
 * - Mobile-responsive design
 */
const AICopilotChatV2 = ({ compact = false }) => {
  const { instance, accounts } = useMsal();
  const user = accounts[0];

  // Streaming hook
  const {
    streamResponse,
    cancelStream,
    isStreaming,
    streamedContent,
    error: streamError,
  } = useStreamingResponse();

  // Conversations hook
  const {
    conversations,
    activeConversationId,
    loadConversations,
    createConversation,
    updateConversation,
    deleteConversation,
    loadMessages,
  } = useConversations();

  // Local state
  const [messages, setMessages] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [currentStreamingMessage, setCurrentStreamingMessage] = useState(null);

  const messagesEndRef = useRef(null);
  const userId = user?.localAccountId || user?.idTokenClaims?.oid;
  const organizationId = user?.idTokenClaims?.extension_OrganizationId || 1;

  // Load conversations on mount
  useEffect(() => {
    loadConversations().catch(error => {
      console.warn('Failed to load conversations (backend not available):', error);
      // Continue without conversations - chat will work in local mode
    });
  }, [loadConversations]);

  // Load messages when conversation changes
  useEffect(() => {
    if (activeConversationId) {
      loadConversationMessages(activeConversationId);
    } else {
      setMessages([]);
    }
  }, [activeConversationId]);

  // Update streaming message content
  useEffect(() => {
    if (isStreaming && streamedContent) {
      setCurrentStreamingMessage({
        role: 'assistant',
        content: streamedContent,
        timestamp: new Date(),
        sources: [],
        isStreaming: true,
      });
    }
  }, [isStreaming, streamedContent]);

  // Handle stream errors
  useEffect(() => {
    if (streamError) {
      antMessage.error(streamError);
      setCurrentStreamingMessage(null);
    }
  }, [streamError]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, currentStreamingMessage]);

  const loadConversationMessages = async (conversationId) => {
    setIsLoadingHistory(true);
    try {
      const msgs = await loadMessages(conversationId);
      setMessages(msgs || []);
    } catch (error) {
      console.error('Failed to load messages:', error);
      antMessage.error('Failed to load conversation history');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const fetchSuggestions = useCallback(async () => {
    try {
      const response = await aiSummarizationService.getSuggestedQuestions({
        userId,
        organizationId,
        conversationId: activeConversationId,
      });

      if (response.data?.questions) {
        setSuggestions(response.data.questions);
      }
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    }
  }, [userId, organizationId, activeConversationId]);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions, messages.length]);

  const handleSendMessage = async (content) => {
    if (!content.trim()) return;

    // Create or get conversation
    let conversationId = activeConversationId;
    if (!conversationId) {
      try {
        // Generate title from first message (first 50 chars)
        const title = content.slice(0, 50) + (content.length > 50 ? '...' : '');
        const newConv = await createConversation(title);
        conversationId = newConv.conversationId;
      } catch (error) {
        console.warn('Could not create conversation (backend not available):', error);
        // Continue without conversation ID - chat will work in local mode
      }
    }

    // Add user message immediately
    const userMessage = {
      role: 'user',
      content,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);

    // Save user message to backend (optional if backend is available)
    if (conversationId) {
      await saveMessage(conversationId, userMessage).catch(err => {
        console.warn('Could not save message:', err);
      });
    }

    // Start streaming AI response
    await streamResponse(content, {
      userId,
      organizationId,
      conversationId,
      onComplete: async (data) => {
        // Create final AI message with sources
        const aiMessage = {
          role: 'assistant',
          content: data.content,
          sources: data.sources || [],
          metadata: data.metadata || {},
          timestamp: new Date(),
        };

        // Add to messages
        setMessages((prev) => [...prev, aiMessage]);

        // Clear streaming message
        setCurrentStreamingMessage(null);

        // Save AI message to backend (optional if backend is available)
        if (conversationId) {
          await saveMessage(conversationId, aiMessage).catch(err => {
            console.warn('Could not save AI message:', err);
          });

          // Update conversation metadata
          await updateConversation(conversationId, {
            messageCount: messages.length + 2,
            updatedAt: new Date(),
          }).catch(err => {
            console.warn('Could not update conversation:', err);
          });
        }
      },
    });
  };

  const saveMessage = async (conversationId, message) => {
    try {
      await aiSummarizationService.saveConversationMessage({
        conversationId,
        role: message.role,
        content: message.content,
        sources: message.sources,
        timestamp: message.timestamp,
      });
    } catch (error) {
      console.error('Failed to save message:', error);
    }
  };

  const handleStopGeneration = () => {
    cancelStream();
    setCurrentStreamingMessage(null);
  };

  const handleRegenerateMessage = async (message) => {
    // Find the user message that prompted this AI response
    const messageIndex = messages.findIndex((m) => m === message);
    const userMessage = messages[messageIndex - 1];

    if (userMessage && userMessage.role === 'user') {
      // Remove the AI message
      setMessages((prev) => prev.filter((m) => m !== message));

      // Re-send the user's query
      await handleSendMessage(userMessage.content);
    }
  };

  const handleMessageFeedback = async (message, feedbackType) => {
    try {
      await aiSummarizationService.submitFeedback({
        summaryId: message.id || generateTempId(),
        helpful: feedbackType === 'positive',
        feedbackType: 'MESSAGE_QUALITY',
        userId,
        organizationId,
      });

      antMessage.success('Thank you for your feedback!');
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      antMessage.error('Failed to submit feedback');
    }
  };

  const handleMessageCopy = async (message) => {
    try {
      await navigator.clipboard.writeText(message.content);
      antMessage.success('Copied to clipboard');
    } catch (error) {
      antMessage.error('Failed to copy');
    }
  };

  const handleMessageShare = async (message) => {
    // Future: Implement share functionality
    antMessage.info('Share feature coming soon');
  };

  const handleNewConversation = async () => {
    try {
      const title = `New Chat ${new Date().toLocaleString()}`;
      await createConversation(title);
      setMessages([]);
      setSidebarVisible(false);
    } catch (error) {
      console.warn('Conversation API not available, working in local mode:', error);
      // Allow chat to work without backend - just clear messages
      setMessages([]);
      setSidebarVisible(false);
      antMessage.info('Working in demo mode - conversation history not available');
    }
  };

  const handleSelectConversation = async (conversation) => {
    await loadConversationMessages(conversation.conversationId);
    setSidebarVisible(false);
  };

  const handleDeleteConversation = async (conversationId) => {
    await deleteConversation(conversationId);

    if (conversationId === activeConversationId) {
      setMessages([]);
    }
  };

  const handleRenameConversation = async (conversationId, newTitle) => {
    await updateConversation(conversationId, { title: newTitle });
  };

  const handleClearConversation = async () => {
    try {
      if (activeConversationId) {
        await handleDeleteConversation(activeConversationId);
      }
    } catch (error) {
      console.warn('Conversation API not available:', error);
    }
    // Always clear messages locally
    setMessages([]);
  };

  const handleExampleClick = (example) => {
    handleSendMessage(example);
  };

  const handleSuggestionClick = (suggestionText) => {
    handleSendMessage(suggestionText);
  };

  const generateTempId = () => {
    return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Combine regular messages with streaming message
  const displayMessages = currentStreamingMessage
    ? [...messages, currentStreamingMessage]
    : messages;

  const showWelcome = messages.length === 0 && !isLoadingHistory && !isStreaming;

  return (
    <div
      className={`${styles.chatV2Container} ${compact ? styles.chatV2ContainerCompact : ''}`}
    >
      {/* Header */}
      <div className={styles.chatHeader}>
        <div className={styles.headerLeft}>
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setSidebarVisible(true)}
            className={styles.headerButton}
            aria-label="Open conversation history"
          />
          <div className={styles.headerTitleContainer}>
            <h2 className={styles.headerTitle}>
              {activeConversationId
                ? conversations.find((c) => c.conversationId === activeConversationId)
                    ?.title || 'BICollab Intelligence'
                : 'BICollab Intelligence'}
            </h2>
            {!activeConversationId && (
              <span className={styles.headerSubtitle}>AI-Powered Analytics Assistant</span>
            )}
          </div>
        </div>

        <div className={styles.headerRight}>
          {messages.length > 0 && (
            <Button
              type="text"
              icon={<DeleteOutlined />}
              onClick={handleClearConversation}
              className={styles.headerButton}
              danger
              aria-label="Clear conversation"
            >
              Clear
            </Button>
          )}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleNewConversation}
            className={styles.headerButton}
          >
            New Chat
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.chatContent}>
        {isLoadingHistory ? (
          <div className={styles.loadingContainer}>
            <Spin size="large" tip="Loading conversation..." />
          </div>
        ) : showWelcome ? (
          <WelcomeScreen
            onSelectExample={handleExampleClick}
            userName={user?.name || user?.idTokenClaims?.name}
            compact={compact}
          />
        ) : (
          <>
            <MessageList
              messages={displayMessages}
              isStreaming={isStreaming}
              onRegenerate={handleRegenerateMessage}
              onFeedback={handleMessageFeedback}
              onCopy={handleMessageCopy}
              onShare={handleMessageShare}
              user={user}
            />
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Suggestions */}
      {!isStreaming && messages.length > 0 && suggestions.length > 0 && (
        <div className={styles.suggestionsContainer}>
          <SuggestionCards
            suggestions={suggestions}
            onSelectSuggestion={handleSuggestionClick}
          />
        </div>
      )}

      {/* Input */}
      <div className={styles.inputContainer}>
        <ChatInput
          onSubmit={handleSendMessage}
          onStop={handleStopGeneration}
          isLoading={isStreaming}
          disabled={isLoadingHistory}
          placeholder={
            messages.length === 0
              ? 'Ask me anything about your data...'
              : 'Ask a follow-up question...'
          }
        />
      </div>

      {/* Conversation Sidebar */}
      <ConversationSidebar
        visible={sidebarVisible}
        onClose={() => setSidebarVisible(false)}
        conversations={conversations}
        activeConversationId={activeConversationId}
        onSelectConversation={handleSelectConversation}
        onNewConversation={handleNewConversation}
        onDeleteConversation={handleDeleteConversation}
        onRenameConversation={handleRenameConversation}
      />
    </div>
  );
};

export default AICopilotChatV2;
